import type { AxiosRequestConfig } from "axios";
import { axiosInstance } from "./interceptors";

/**
 * Generic API request işlemi
 * @param endpoint - API endpoint (örn: '/api/user/profile')
 * @param method - HTTP method
 * @param data - Request body (POST, PUT için)
 * @param options - Axios request options
 * @returns Direct backend response data (T)
 */
export async function performRequest<T>(
    endpoint: string,
    method: string,
    data?: unknown,
    options: AxiosRequestConfig = {}
): Promise<T> {
    try {
        const response = await axiosInstance.request<T>({
            ...options,
            url: endpoint,
            method,
            data: data === undefined ? options.data : data,
            headers: {
                ...options.headers,
            },
        });

        return response.data;
    } catch (error) {
        // Hata zaten interceptor'da loglandı
        // Burası sadece ek context için kullanılabilir
        if (!endpoint.endsWith('/lifecycle') && typeof window !== 'undefined' && (window as Window & { APP_DEBUG?: boolean }).APP_DEBUG) {
            console.error(`[API Request Failed] ${method} ${endpoint}`, {
                data,
                error
            });
        }
        throw error; // Hatayı yukarı fırlat
    }
}

/**
 * API Client - tip güvenli HTTP istekleri için wrapper
 */
export const apiClient = {
    /**
     * Generic request method
     */
    request: performRequest,

    /**
     * GET request
     */
    get: <T>(endpoint: string, options?: AxiosRequestConfig) =>
        performRequest<T>(endpoint, 'GET', undefined, options),

    /**
     * POST request
     */
    post: <T>(endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        performRequest<T>(endpoint, 'POST', data, options),

    /**
     * PUT request
     */
    put: <T>(endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        performRequest<T>(endpoint, 'PUT', data, options),

    /**
     * PATCH request
     */
    patch: <T>(endpoint: string, data?: unknown, options?: AxiosRequestConfig) =>
        performRequest<T>(endpoint, 'PATCH', data, options),

    /**
     * DELETE request
     */
    delete: <T>(endpoint: string, options?: AxiosRequestConfig) =>
        performRequest<T>(endpoint, 'DELETE', undefined, options),
};
